import React, { useState, useEffect, useMemo } from "react";
import { API, UPLOADS } from "./../../config/api";
export default function Manageuser() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [previewImage, setPreviewImage] = useState("");

  const [statusFilter, setStatusFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 25;

  // --- UPDATED SEARCH STATE ---
  const [mainFilters, setMainFilters] = useState({
    userId: "",
    phone: "",
  });

  // Modal States
  const [showUserModal, setShowUserModal] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Table Edit States
  const [editingUserId, setEditingUserId] = useState(null);

  const [savingTableUser, setSavingTableUser] = useState(false);

  const [tableEditForm, setTableEditForm] = useState({
    name: "",
    phone: "",
    email: "",
    city: "",
    state: "",
    account_type: "",
    status: "",
    password: "",
    provider_options: [],
    supplier_types: [],
  });

  // Filters for Orders within the User Modal
  const [orderFilters, setOrderFilters] = useState({
    fromDate: "",
    toDate: "",
    orderId: "",
  });

  // --- UPDATED FILTER LOGIC FOR MAIN TABLE ---
  const filteredUsers = useMemo(() => {
    return users.filter((u) => {
      const matchesId = mainFilters.userId
        ? u.id.toString().includes(mainFilters.userId)
        : true;

      const matchesPhone = mainFilters.phone
        ? u.phone.toString().includes(mainFilters.phone)
        : true;

      const matchesStatus =
        statusFilter === "All"
          ? true
          : u.status?.toLowerCase() === statusFilter.toLowerCase();

      return matchesId && matchesPhone && matchesStatus;
    });
  }, [users, mainFilters, statusFilter]);
  // Close modals/popups with Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key !== "Escape") return;

      // Close image preview first
      if (previewImage) {
        setPreviewImage("");
        return;
      }

      // Close order details modal
      if (selectedOrder) {
        setSelectedOrder(null);
        return;
      }

      // Close user profile modal
      if (showUserModal) {
        handleCloseAll();
        return;
      }

      // Close table edit mode if active
      if (editingUserId !== null) {
        handleCancelTableEdit();
        return;
      }
    };

    window.addEventListener("keydown", handleEscape);

    return () => {
      window.removeEventListener("keydown", handleEscape);
    };
  }, [previewImage, selectedOrder, showUserModal, editingUserId]);
  const getFilteredOrders = () => {
    if (!userDetails) return [];
    return userDetails.orders.filter((order) => {
      const orderDate = new Date(order.created_at).toISOString().split("T")[0];
      const matchesFrom = orderFilters.fromDate
        ? orderDate >= orderFilters.fromDate
        : true;
      const matchesTo = orderFilters.toDate
        ? orderDate <= orderFilters.toDate
        : true;
      const matchesId = orderFilters.orderId
        ? order.id.toString().includes(orderFilters.orderId)
        : true;

      return matchesFrom && matchesTo && matchesId;
    });
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await fetch(API.LIST_USERS);
      const data = await res.json();
      if (data.success) setUsers(data.users);
    } catch (err) {
      console.error("Error fetching users", err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewUser = async (user) => {
    try {
      const res = await fetch(`${API.USER_DETAILS}&id=${user.id}`);
      const data = await res.json();
      if (data.success) {
        setUserDetails(data);
        setShowUserModal(true);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleCloseAll = () => {
    setShowUserModal(false);
    setUserDetails(null);
    setSelectedOrder(null);
  };

  const handleBlockStatus = async (action) => {
    const newStatus = action === "block" ? "blocked" : "active";

    if (!window.confirm(`Are you sure you want to ${action} this user?`))
      return;

    try {
      const url = action === "block" ? API.BLOCK_USER : API.UNBLOCK_USER;
      const res = await fetch(`${url}&id=${userDetails.user.id}`);
      const data = await res.json();

      if (data.success) {
        // 1. Update the Main Table Background
        setUsers((prevUsers) =>
          prevUsers.map((u) =>
            u.id === userDetails.user.id ? { ...u, status: newStatus } : u,
          ),
        );

        // 2. Update the currently open Modal Details
        setUserDetails((prev) => ({
          ...prev,
          user: { ...prev.user, status: newStatus },
        }));

        alert(`User has been ${newStatus} successfully.`);
      }
    } catch (err) {
      console.error("Status update failed", err);
      alert("Action failed. Please check your connection.");
    }
  };
  const handleViewOrderDetails = async (orderId) => {
    try {
      const res = await fetch(API.ALL_ORDERS);
      const data = await res.json();
      if (data.success) {
        const fullOrder = data.orders.find((o) => o.id === orderId);
        setSelectedOrder(fullOrder);
      }
    } catch (err) {
      console.error("Error fetching detailed order info", err);
      alert("Could not load order items.");
    }
  };

  const getPageNumbers = () => {
    const pages = [];

    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, "...", totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, "...", totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, "...", currentPage, "...", totalPages);
      }
    }

    return pages;
  };

  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * usersPerPage;
    return filteredUsers.slice(startIndex, startIndex + usersPerPage);
  }, [filteredUsers, currentPage]);
  useEffect(() => {
    setCurrentPage(1);
  }, [mainFilters, statusFilter]);

  if (loading) return <div className="text-center mt-5">Loading Users...</div>;
  const handleTableEdit = (user) => {
    let providerOptions = [];
    let supplierTypes = [];

    try {
      providerOptions = Array.isArray(user.provider_options)
        ? user.provider_options
        : JSON.parse(user.provider_options || "[]");
    } catch {
      providerOptions = [];
    }

    try {
      supplierTypes = Array.isArray(user.supplier_types)
        ? user.supplier_types
        : JSON.parse(user.supplier_types || "[]");
    } catch {
      supplierTypes = [];
    }

    setEditingUserId(user.id);

    setTableEditForm({
      name: user.name || "",
      phone: user.phone || "",
      email: user.email || "",
      city: user.city || "",
      state: user.state || "",
      account_type: user.account_type || "",
      status: user.status || "",
      password: "",
      provider_options: providerOptions,
      supplier_types: supplierTypes,
    });
  };

  const handleTableEditChange = (e) => {
    const { name, value } = e.target;

    setTableEditForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCancelTableEdit = () => {
    setEditingUserId(null);
    setTableEditForm({});
  };
  const handleSaveTableUser = async (userId) => {
    if (!tableEditForm.password?.trim()) {
      alert("Please enter a new password.");
      return;
    }

    try {
      setSavingTableUser(true);

      const formData = new FormData();

      formData.append("id", userId);
      formData.append("password", tableEditForm.password.trim());

      const res = await fetch(API.UPDATE_USER_ADMIN, {
        method: "POST",
        body: formData,
      });

      const data = await res.json();

      if (!data.success) {
        alert(data.message || "Password update failed.");
        return;
      }

      // Update password in the table
      setUsers((prevUsers) =>
        prevUsers.map((u) =>
          u.id === userId
            ? {
                ...u,
                password: tableEditForm.password.trim(),
              }
            : u,
        ),
      );

      alert("Password updated successfully.");

      setEditingUserId(null);
      setTableEditForm({
        password: "",
      });
    } catch (error) {
      console.error("Password update error:", error);
      alert("Something went wrong while updating password.");
    } finally {
      setSavingTableUser(false);
    }
  };
  return (
    <div className="container-fluid  min-vh-100">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="machine-title">Customer Management</h2>
        <span className="badge bg-teal p-2 px-3">
          {users.length} Total Users
        </span>
      </div>

      {/* --- NEW SEARCH BAR UI FOR MAIN TABLE --- */}
      <div className="row g-3 mb-4 customer-mgmt-filter-row no-print">
        <div className="col-md-3">
          <div className="customer-filter-box">
            <span className="customer-filter-icon">
              <i className="bi bi-hash"></i>
            </span>

            <input
              type="text"
              className="customer-filter-field"
              placeholder="Search by ID..."
              value={mainFilters.userId}
              onChange={(e) =>
                setMainFilters({ ...mainFilters, userId: e.target.value })
              }
            />
          </div>
        </div>

        <div className="col-md-4">
          <div className="customer-filter-box">
            <span className="customer-filter-icon">
              <i className="bi bi-telephone"></i>
            </span>

            <input
              type="text"
              className="customer-filter-field"
              placeholder="Search by Phone Number..."
              value={mainFilters.phone}
              onChange={(e) =>
                setMainFilters({ ...mainFilters, phone: e.target.value })
              }
            />
          </div>
        </div>

        <div className="col-md-3">
          <select
            className="form-select customer-mgmt-filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="All">All Status</option>
            <option value="active">Active</option>
            <option value="blocked">Blocked</option>
          </select>
        </div>

        {(mainFilters.userId || mainFilters.phone) && (
          <div className="col-md-2 d-flex align-items-center">
            <button
              className="customer-mgmt-filter-clear-btn"
              onClick={() => setMainFilters({ userId: "", phone: "" })}
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {/* MAIN USERS TABLE */}
      <div className="table-responsive bg-white rounded shadow-sm p-0">
        <table className="table table-hover align-middle mb-0">
          <thead>
            <tr className="bg-light text-muted small text-center">
              <th className="ps-4">ID</th>
              <th className="text-center">IMAGE</th>
              <th>User Details</th>
              <th>CREATED DATE</th>

              <th>CITY</th>
              <th>ACCOUNT_TYPE</th>
              <th>PASSWORD</th>
              <th>STATUS</th>
              <th className="text-end pe-4">ACTION</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length > 0 ? (
              paginatedUsers.map((u) => {
                const isEditing = editingUserId === u.id;

                let providerOptions = [];

                try {
                  providerOptions = Array.isArray(u.provider_options)
                    ? u.provider_options
                    : JSON.parse(u.provider_options || "[]");
                } catch {
                  providerOptions = [];
                }

                return (
                  <tr key={u.id}>
                    {/* ID */}
                    <td className="ps-4 fw-bold text-teal">{u.id}</td>

                    {/* IMAGE */}
                    <td className="text-center">
                      {u.profile_image ? (
                        <img
                          src={`${API.BASE_URL}/uploads/profile/${u.profile_image}`}
                          alt={u.name}
                          onClick={() =>
                            setPreviewImage(
                              `${API.BASE_URL}/uploads/profile/${u.profile_image}`,
                            )
                          }
                          onError={(e) => {
                            e.target.style.display = "none";

                            if (e.target.nextSibling) {
                              e.target.nextSibling.style.display =
                                "inline-flex";
                            }
                          }}
                          style={{
                            width: "45px",
                            height: "45px",
                            borderRadius: "50%",
                            objectFit: "cover",
                            cursor: "pointer",
                            border: "1px solid #ddd",
                          }}
                        />
                      ) : null}

                      <div
                        style={{
                          display: u.profile_image ? "none" : "inline-flex",
                          width: "45px",
                          height: "45px",
                          borderRadius: "50%",
                          background: "#f3f4f6",
                          alignItems: "center",
                          justifyContent: "center",
                          border: "1px solid #ddd",
                        }}
                      >
                        <i
                          className="bi bi-person-fill"
                          style={{
                            fontSize: "22px",
                            color: "#6c757d",
                          }}
                        ></i>
                      </div>
                    </td>

                    {/* NAME / PHONE */}
                    <td>
                      {/* {isEditing ? (
                        <>
                          <input
                            type="text"
                            name="name"
                            className="form-control form-control-sm mb-1"
                            value={tableEditForm.name}
                            onChange={handleTableEditChange}
                            placeholder="Name"
                          />

                          <input
                            type="text"
                            name="phone"
                            className="form-control form-control-sm"
                            value={tableEditForm.phone}
                            onChange={handleTableEditChange}
                            placeholder="Phone"
                          />
                        </>
                      ) : (
                        <>
                          <div className="fw-bold">{u.name}</div>
                          <div className="text-muted small">{u.phone}</div>
                        </>
                      )} */}
                      <div className="fw-bold">{u.name}</div>
                      <div className="text-muted small">{u.phone}</div>
                      <div className="text-muted small">{u.email}</div>
                    </td>

                    <td>
                      <div className="fw-bold">
                        {new Date(u.created_at).toLocaleDateString("en-IN")}
                      </div>

                      <div className="text-muted small">
                        {new Date(u.created_at).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </div>
                    </td>

                    <td>
                      {/* {isEditing ? (
                        <>
                          <input
                            type="text"
                            name="city"
                            className="form-control form-control-sm mb-1"
                            value={tableEditForm.city}
                            onChange={handleTableEditChange}
                            placeholder="City"
                          />

                          <input
                            type="text"
                            name="state"
                            className="form-control form-control-sm"
                            value={tableEditForm.state}
                            onChange={handleTableEditChange}
                            placeholder="State"
                          />
                        </>
                      ) : (
                        <>
                          <div className="fw-bold">{u.city}</div>
                          <div className="text-muted small">{u.state}</div>
                        </>
                      )} */}
                      <div className="fw-bold">{u.city}</div>
                      <div className="text-muted small">{u.state}</div>
                    </td>

                    {/* ACCOUNT TYPE */}
                    <td>
                      {/* {isEditing ? (
                        <>
                          <input
                            type="text"
                            name="account_type"
                            className="form-control form-control-sm mb-1"
                            value={tableEditForm.account_type}
                            onChange={handleTableEditChange}
                            placeholder="Account Type"
                          />

                          <div
                            className="text-muted small"
                            style={{
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                            }}
                          >
                            {providerOptions.length > 0 &&
                              Array.from(
                                {
                                  length: Math.ceil(providerOptions.length / 3),
                                },
                                (_, i) =>
                                  providerOptions.slice(i * 3, i * 3 + 3),
                              ).map((row, index) => (
                                <div key={index}>{row.join(", ")}</div>
                              ))}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="fw-bold">{u.account_type}</div>

                          <div
                            className="text-muted small"
                            style={{
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                            }}
                          >
                            {providerOptions.length > 0 &&
                              Array.from(
                                {
                                  length: Math.ceil(providerOptions.length / 3),
                                },
                                (_, i) =>
                                  providerOptions.slice(i * 3, i * 3 + 3),
                              ).map((row, index) => (
                                <div key={index}>{row.join(", ")}</div>
                              ))}
                          </div>
                        </>
                      )} */}
                      <div className="fw-bold">{u.account_type}</div>

                      <div
                        className="text-muted small"
                        style={{
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        }}
                      >
                        {providerOptions.length > 0 &&
                          Array.from(
                            {
                              length: Math.ceil(providerOptions.length / 3),
                            },
                            (_, i) => providerOptions.slice(i * 3, i * 3 + 3),
                          ).map((row, index) => (
                            <div key={index}>{row.join(", ")}</div>
                          ))}
                      </div>
                    </td>

                    {/* PASSWORD */}
                    <td>
                      {isEditing ? (
                        <input
                          type="text"
                          name="password"
                          className="form-control form-control-sm"
                          value={tableEditForm.password}
                          onChange={handleTableEditChange}
                          placeholder="New password"
                        />
                      ) : (
                        u.password
                      )}
                    </td>

                    {/* STATUS */}
                    <td>
                      {/* {isEditing ? (
                        <select
                          name="status"
                          className="form-select form-select-sm"
                          value={tableEditForm.status}
                          onChange={handleTableEditChange}
                        >
                          <option value="active">Active</option>
                          <option value="blocked">Blocked</option>
                        </select>
                      ) : (
                        <span
                          className={`status-pill ${u.status?.toLowerCase()}`}
                        >
                          {u.status}
                        </span>
                      )} */}
                      <span
                        className={`status-pill ${u.status?.toLowerCase()}`}
                      >
                        {u.status}
                      </span>
                    </td>

                    {/* ACTION */}
                    <td className="text-center">
                      {isEditing ? (
                        <div className="d-flex justify-content-center gap-1">
                          <button
                            type="button"
                            className="btn btn-sm btn-success  p-2 pt-1 pb-1"
                            disabled={savingTableUser}
                            onClick={() => handleSaveTableUser(u.id)}
                            title="Save"
                          >
                            {savingTableUser ? (
                              <span
                                className="spinner-border spinner-border-sm"
                                role="status"
                              ></span>
                            ) : (
                              <i className="bi bi-check-lg"></i>
                            )}
                          </button>

                          <button
                            type="button"
                            className="btn btn-sm btn-secondary p-2 pt-1 pb-1"
                            disabled={savingTableUser}
                            onClick={handleCancelTableEdit}
                            title="Cancel"
                          >
                            <i className="bi bi-x-lg"></i>
                          </button>
                        </div>
                      ) : (
                        <div className="d-flex justify-content-center gap-1">
                          {/* EDIT */}
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary p-2 pt-1 pb-1"
                            onClick={() => handleTableEdit(u)}
                            title="Edit User"
                          >
                            <i className="bi bi-pencil"></i>
                          </button>

                          {/* VIEW */}
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-teal p-2 pt-1 pb-1"
                            onClick={() => handleViewUser(u)}
                            title="View User"
                          >
                            <i className="bi bi-eye"></i>
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="9" className="text-center py-5 text-muted">
                  No users found matching your search.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="d-flex justify-content-center align-items-center mt-3">
        <nav>
          <ul className="pagination mb-0">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Previous
              </button>
            </li>
            {getPageNumbers().map((page, index) => (
              <li
                key={index}
                className={`page-item ${
                  currentPage === page ? "active" : ""
                } ${page === "..." ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  disabled={page === "..."}
                  onClick={() => page !== "..." && setCurrentPage(page)}
                >
                  {page}
                </button>
              </li>
            ))}

            <li
              className={`page-item ${
                currentPage === totalPages || totalPages === 0 ? "disabled" : ""
              }`}
            >
              <button
                className="page-link"
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </button>
            </li>
          </ul>
        </nav>
      </div>

      {/* ... (Modals and Styles remain same as your provided code) ... */}

      {/* MODAL 1: USER PROFILE & ORDER LIST */}
      {showUserModal && userDetails && (
        <div className="custom-modal-overlay" onClick={handleCloseAll}>
          <div
            className="custom-modal-card"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header-custom">
              <h5 className="mb-0 fw-bold">User Profile & History</h5>
            </div>

            <div className="modal-body-custom">
              <div className="row mb-4 border-bottom pb-3">
                <div className="col-md-6">
                  {/* <label>Personal Information</label> */}
                  <p className="fw-bold mb-0" style={{ fontSize: "1.2rem" }}>
                    {userDetails.user.name}
                  </p>
                  <p className="text-muted small">
                    {userDetails.user.phone} | {userDetails.user.email}{" "}
                  </p>
                </div>
                <div className="col-md-6 text-md-end">
                  <label className="text-muted small d-block">
                    Account Status
                  </label>
                  <span
                    className={`status-pill ${userDetails.user.status?.toLowerCase()}`}
                  >
                    {userDetails.user.status}
                  </span>
                </div>
              </div>

              <div className="d-flex justify-content-between align-items-center mb-3">
                <label className="mb-0">Recent Orders</label>
                <div className="d-flex align-items-center gap-2">
                  <label className="small fw-bold text-muted mb-0">From:</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={orderFilters.fromDate}
                    onChange={(e) =>
                      setOrderFilters({
                        ...orderFilters,
                        fromDate: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="d-flex align-items-center gap-2">
                  <label className="small fw-bold text-muted mb-0">To:</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={orderFilters.toDate}
                    onChange={(e) =>
                      setOrderFilters({
                        ...orderFilters,
                        toDate: e.target.value,
                      })
                    }
                  />
                </div>
                {(orderFilters.fromDate || orderFilters.toDate) && (
                  <button
                    className="btn btn-sm btn-link text-danger p-0"
                    onClick={() =>
                      setOrderFilters({ fromDate: "", toDate: "" })
                    }
                  >
                    Clear
                  </button>
                )}
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Order ID"
                  style={{ width: "100px" }}
                  onChange={(e) =>
                    setOrderFilters({
                      ...orderFilters,
                      orderId: e.target.value,
                    })
                  }
                />
              </div>

              <div className="table-responsive border rounded">
                <table className="table table-sm align-middle mb-0">
                  <thead className="bg-light">
                    <tr className="small text-muted">
                      <th className="text-center">Order ID</th>
                      <th className="text-center">Date</th>
                      <th className="text-center">Time</th>
                      <th className="text-center">Amount</th>
                      <th className="text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getFilteredOrders().length > 0 ? (
                      getFilteredOrders().map((order) => (
                        <tr key={order.id}>
                          <td className="ps-3 fw-bold">{order.id}</td>
                          <td>
                            {new Date(order.created_at).toLocaleDateString()}
                          </td>
                          <td className="text-muted small">
                            {new Date(order.created_at).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </td>
                          <td className="fw-bold text-teal">
                            ₹{order.total_amount}
                          </td>
                          <td className="text-center">
                            <button
                              className="btn btn-sm btn-teal"
                              onClick={() => handleViewOrderDetails(order.id)}
                            >
                              View Details
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center py-3">
                          No orders match these filters
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="modal-footer-custom">
              {/* Check status to toggle between Unblock and Block buttons */}
              {userDetails.user.status?.toLowerCase() === "blocked" ? (
                <button
                  onClick={() => handleBlockStatus("unblock")}
                  className="btn btn-success btn-block-action me-2"
                >
                  <i className="bi bi-unlock-fill me-1"></i> Unblock User
                </button>
              ) : (
                <button
                  onClick={() => handleBlockStatus("block")}
                  className="btn btn-danger btn-block-action me-2"
                >
                  <i className="bi bi-slash-circle me-1"></i> Block User
                </button>
              )}
              <button className="btn btn-light border" onClick={handleCloseAll}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 2: DETAILED ORDER INFO */}
      {selectedOrder && (
        <div
          className="custom-modal-overlay second-modal"
          onClick={() => setSelectedOrder(null)}
        >
          <div
            className="custom-modal-card animate-slide-up"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "850px" }}
          >
            <div className="modal-header-custom bg-dark text-white">
              <h5 className="mb-0 fw-bold">
                Order Details #{selectedOrder.order_no || selectedOrder.id}
              </h5>
              <button
                className="close-btn text-white"
                onClick={() => setSelectedOrder(null)}
              >
                &times;
              </button>
            </div>

            <div className="modal-body-custom">
              <div className="row mb-4 border-bottom pb-3">
                <div className="col-md-6">
                  <label>Order Information</label>
                  <p className="fw-bold mb-0">
                    Status:{" "}
                    <span
                      className={`status-pill ${selectedOrder.status.toLowerCase()}`}
                    >
                      {selectedOrder.status}
                    </span>
                  </p>
                </div>
                <div className="col-md-6 text-md-end">
                  <label>Total Amount</label>
                  <h4 className="fw-bold text-teal">
                    ₹{selectedOrder.total_amount}
                  </h4>
                </div>
              </div>

              <label className="mb-3">Items in this Order</label>
              <div className="table-responsive border rounded">
                <table className="table table-sm align-middle mb-0">
                  <thead className="bg-light">
                    <tr className="small text-muted">
                      <th className="text-center">Preview</th>
                      <th className="text-center">Design code</th>
                      <th className="text-center">Specifications</th>
                      {/* <th>Concept</th> 
                      <th>Details</th>
                      <th>Download</th> */}
                      <th className="text-center">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedOrder.items && selectedOrder.items.length > 0 ? (
                      selectedOrder.items.map((item, index) => {
                        return (
                          <tr key={index}>
                            <td className="ps-3 py-2">
                              <img
                                src={`${UPLOADS.DESIGN_IMAGE1}${item.user_id}/${item.jpeg_file}`}
                                alt="Design"
                                className="rounded border bg-white"
                                style={{
                                  width: "60px",
                                  height: "60px",
                                  objectFit: "contain",
                                }}
                                onError={(e) => {
                                  e.target.src =
                                    "https://via.placeholder.com/60";
                                }}
                              />
                            </td>
                            <td>
                              <div className="fw-bold small">
                                {item.design_code}
                              </div>
                            </td>
                            <td>
                              <div className="small">
                                <b>Area:</b> {item.area || ""}
                              </div>
                              <div className="small">
                                <b>Stitches:</b>{" "}
                                {item.stitch || item.stitches || ""}
                              </div>
                              {/* <div className="small">
                                <b>Color:</b>{" "}
                                {item.color || item.colors || " "}
                              </div> */}
                            </td>

                            <td className="text-center  fw-bold small">
                              ₹
                              {item.price
                                ? item.price * (item.qty || 1)
                                : "---"}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="7" className="text-center py-3">
                          No items found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-footer-custom">
              <button
                className="btn btn-dark"
                onClick={() => setSelectedOrder(null)}
              >
                Back to Profile
              </button>
            </div>
          </div>
        </div>
      )}
      {previewImage && (
        <div className="preview-modal" onClick={() => setPreviewImage("")}>
          <div
            className="preview-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="preview-close"
              onClick={() => setPreviewImage("")}
            >
              &times;
            </button>

            <img src={previewImage} alt="Profile Preview" />
          </div>
        </div>
      )}

      <style>{`
        .bg-teal, .btn-teal { background-color: #107a73 !important; color: white !important; }
        .text-teal { color: #126963 !important; }
        .btn-outline-teal { border: 1px solid #00a99d; color: #00a99d; }

  /* Status Pill Base */
  .status-pill {
    padding: 6px 14px;
    border-radius: 50px;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    display: inline-block;
    transition: all 0.3s ease;
  }
.second-modal {
  position: fixed;
  top:0;
  left:0;
  width:100%;
  height:100%;
  z-index:99999;
  background:rgba(0,0,0,0.7);
  display:flex;
  align-items:center;
  justify-content:center;
}
.modal-footer-custom
{
display:"flex";

}

.custom-modal-overlay {
  position:fixed;
  top:0;
  left:0;
  width:100%;
  height:100%;
  display:flex;
  align-items:center;
  justify-content:center;
  z-index:1000;
}
  /* Active/Normal Status - Green */
  .active, .completed {
    background: #e6f4ea !important;
    color: #1e7e34 !important;
    border: 1px solid #c3e6cb;
  }

  /* Blocked/Cancelled Status - Red */
  .blocked, .cancelled {
    background: #fce8e8 !important;
    color: #d93025 !important;
    border: 1px solid #f5c6cb;
  }

  /* Pending Status - Orange/Yellow */
  .pending {
    background: #fff4e5 !important;
    color: #664d03 !important;
    border: 1px solid #ffeeba;
  }

  /* Specific Button styling for Block action */
  .btn-block-action {
    min-width: 120px;
  }

     
        .second-modal { z-index: 1100; background: rgba(0, 0, 0, 0.7); }
        .custom-modal-card { background: white; width: 95%; max-width: 900px; border-radius: 12px; overflow-y: auto; max-height: 90vh; }
        .modal-header-custom { padding: 15px 25px; border-bottom: 1px solid #eee; display: flex; justify-content: space-between; align-items: center; position: sticky; top: 0; z-index: 10; }
        .modal-body-custom { padding: 25px; }
        .modal-footer-custom { padding: 15px 25px; border-top: 1px solid #eee; text-align: right; background: #f8f9fa; }
        .close-btn { background: red; border: none; font-size: 24px; color: #999; }
        .animate-slide-up { animation: slideUp 0.3s ease-out; }
        @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      `}</style>
    </div>
  );
}
